import React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Badge } from '../../../components/ui/badge';

const prettyDate = (dateStr) =>
  new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

/**
 * Menu-change order-impact guard: warns the admin that placed (undelivered)
 * orders contain items this menu change removes (or rely on a delivery
 * override being dropped). Saving never modifies the orders themselves.
 */
const OrderImpactAlert = ({ open, onOpenChange, impact, onProceed, confirmLabel = 'Save anyway' }) => {
  const total = impact?.total_orders_affected || 0;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="order-impact-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {total} placed order{total === 1 ? '' : 's'} affected by this menu change
          </AlertDialogTitle>
          <AlertDialogDescription>
            These orders were placed while the items below were still orderable. Saving will NOT
            change the orders — the bakery is still committed to them. Review and edit or cancel
            them separately if they can't be honored.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-64 overflow-y-auto space-y-3 pr-1" data-testid="order-impact-list">
          {(impact?.dates || []).map((d) => (
            <div key={d.date}>
              <p className="text-xs font-semibold uppercase text-gray-500 mb-1.5">{prettyDate(d.date)}</p>
              <div className="space-y-1.5">
                {d.orders.map((o) => (
                  <div key={o.order_id} className="border rounded-lg p-2.5" data-testid={`impact-order-${o.order_number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <a
                          href={`/admin/orders?search=${o.order_number}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-semibold text-amber-700 hover:underline flex items-center gap-1 shrink-0"
                          data-testid={`impact-order-link-${o.order_number}`}
                        >
                          {o.order_number} <ExternalLink className="h-3 w-3" />
                        </a>
                        <span className="text-xs text-gray-600 truncate">{o.client_name}</span>
                        {o.source === 'recurring' && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full shrink-0">Recurring</span>
                        )}
                      </div>
                      <Badge variant="outline" className="capitalize shrink-0">{(o.status || '').replace(/_/g, ' ')}</Badge>
                    </div>
                    {o.affected_items?.length > 0 && (
                      <p className="text-xs text-red-600 mt-1.5">
                        No longer on this date's menu:{' '}
                        {o.affected_items.map((i) => `${i.quantity}× ${i.product_name}`).join(', ')}
                      </p>
                    )}
                    {o.loses_delivery_override && (
                      <p className="text-xs text-orange-600 mt-1">
                        This date relies on the menu's "deliver to everyone" override for this client's
                        delivery schedule.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="impact-go-back-btn">Go back</AlertDialogCancel>
          <AlertDialogAction
            className="bg-orange-600 hover:bg-orange-700"
            onClick={onProceed}
            data-testid="impact-save-anyway-btn"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default OrderImpactAlert;
